import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  ShieldCheck,
  ArrowLeft,
  UserPlus,
  Info,
  Edit3,
  MapPin,
  UserCheck,
  Search,
  CheckCircle2,
  PlusCircle
} from "lucide-react";
import { Formik, Form, Field } from "formik";
import { addApprover } from "../../store/actions";
import {
  Button,
  Input,
  Select,
  Tabs,
  Breadcrumb,
  useToast,
  Card
} from "../UI";
import { axiosInstance } from "../../config/index";
import { ApproverValidationSchema } from "../../validations/index";
import { useDebounce } from "../../hooks/useDebounce";

const AddApprover = ({ addApprover }: any) => {
  const history = useHistory();
  const { toast } = useToast();
  const [employeeItems, setEmployeeItems] = useState<any>([]);
  const [locationItems, setLocationItems] = useState<any>([]);
  const [searchText, setSearchText] = useState<any>("");
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    axiosInstance.get("/api/sites/getalllocation")
      .then((res) => {
        setLocationItems(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("APPROVER_LOCATIONS_ERROR:", err);
        toast("Failed to fetch locations", "error");
      });
  }, []);

  useEffect(() => {
    if (debouncedSearchText) {
      axiosInstance.get(`/api/requests/search/${debouncedSearchText}`)
        .then((res) => {
          setEmployeeItems(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => console.error(err));
    } else {
      setEmployeeItems([]);
    }
  }, [debouncedSearchText]);

  const employeeOptions = (Array.isArray(employeeItems) ? employeeItems : []).map((op: any) => ({
    label: `${op.oracleId || '???'} - ${op.name || op.fullName || 'Unknown'}`,
    value: op.oracleId || ''
  }));

  const locationOptions = (Array.isArray(locationItems) ? locationItems : []).map((op: any) => ({
    label: String(op.location || op.name || 'Unknown Location'),
    value: String(op.location || op.id || '')
  }));

  const approverRoleOptions = [
    { label: "Security Manager", value: "Security Manager" },
    { label: "Security and Surveillance Supervisor", value: "Security and Surveillance Supervisor" }
  ];

  const tabs = [
    {
      id: 'details',
      label: 'Approver Details',
      icon: <Edit3 className="w-4 h-4" />,
      content: (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field name="oracleId">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Select Employee"
                    placeholder="Search by Oracle ID or name..."
                    options={employeeOptions}
                    value={field.value}
                    onChange={(val) => form.setFieldValue("oracleId", val)}
                    onSearch={(query) => setSearchText(query)}
                    error={meta.touched && meta.error ? meta.error : ""}
                    searchable
                    required
                  />
                )}
              </Field>

              <Field name="approverRole">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Authority Level"
                    placeholder="Select role..."
                    options={approverRoleOptions}
                    value={field.value}
                    onChange={(val) => form.setFieldValue("approverRole", val)}
                    error={meta.touched && meta.error ? meta.error : ""}
                    required
                  />
                )}
              </Field>

              <Field name="locations">
                {({ field, form, meta }: any) => (
                  <Select
                    label="Assigned Jurisdictions"
                    placeholder="Select locations..."
                    options={locationOptions}
                    value={field.value}
                    onChange={(val) => form.setFieldValue("locations", val)}
                    error={meta.touched && meta.error ? meta.error : ""}
                    multiple
                    searchable
                    required
                  />
                )}
              </Field>
            </div>

            <div className="space-y-6">
              <div className="p-4 bg-[#8dc63f]/10/50 rounded-2xl border border-[#8dc63f]/20 flex gap-4">
                <div className="p-2 bg-white rounded-xl shadow-sm h-fit">
                  <UserPlus className="w-4 h-4 text-[#8dc63f]" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-700">Privileged Onboarding</p>
                  <p className="text-xs font-medium text-[#8dc63f]/70 leading-relaxed">
                    Assigning a user as an approver grants them administrative rights to review access requests.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-[#8dc63f]/10/50 rounded-2xl border border-[#8dc63f]/20 flex gap-4">
                <div className="p-2 bg-white rounded-xl shadow-sm h-fit">
                  <ShieldCheck className="w-4 h-4 text-[#8dc63f]" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-bold text-slate-700">Regional Governance</p>
                  <p className="text-xs font-medium text-[#8dc63f]/70 leading-relaxed">
                    Ensure the locations are accurate to prevent unauthorized access approvals outside their scope.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex flex-col gap-4">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Approvers', to: '/approverlist' },
            { label: 'Add Approver' }
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#8dc63f] rounded-2xl shadow-lg shadow-[#8dc63f]/30">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-none">Add Approver</h1>
              <p className="text-sm text-slate-400 font-medium mt-1 uppercase tracking-widest">Onboard new authority member</p>
            </div>
          </div>

          <Link to="/approverlist">
            <Button variant="ghost" className="gap-2 text-slate-400 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" />
              Back to List
            </Button>
          </Link>
        </div>
      </div>

      <Formik
        initialValues={{
          oracleId: "",
          locations: [],
          approverRole: ""
        }}
        validationSchema={ApproverValidationSchema}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          try {
            await addApprover(values);
            toast("Approver added successfully", "success");
            history.push("/approverlist");
          } catch (error: any) {
            toast(error.message || "Failed to add approver", "error");
            setErrors(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} className="space-y-8">
            <Card>
              {tabs[0].content}
            </Card>

            <div className="flex items-center justify-end gap-3 pt-4 px-1">
              <Link to="/approverlist">
                <Button type="button" variant="ghost" className="h-12 px-8 text-slate-500 hover:text-slate-900">Cancel</Button>
              </Link>
              <Button
                type="submit"
                className="h-12 px-10 gap-2 shadow-xl shadow-[#8dc63f]/20 bg-[#8dc63f] hover:bg-[#76ab32]"
                disabled={isSubmitting}
              >
                <PlusCircle className="w-4 h-4" />
                {isSubmitting ? 'Creating Profile...' : 'Add Approver'}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  addApprover,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddApprover);




